import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FaChevronDown, FaChevronUp } from "react-icons/fa6";
import { Download as DownloadIcon } from "lucide-react";
import { CiFileOn, CiImageOn } from "react-icons/ci";
import toast from "react-hot-toast";

import ChartsPanel from "../../../../components/patient-detail/details-tab/ChartsPanel";
import { exportPatientReadings } from "../../../../services/patient/documents";
import { buildReportFilename, DOCTYPES } from "../../../../utils/fileNaming";
import { Dropdown, DropdownItem } from "../../../../components/ui/dropdown";
import { ReactComponent as ShareSvg } from "../../../../assets/icons/chart/chart-share.svg";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { formatReadingDate } from "./format";
import styles from "./ReadingReviewSession.module.css";

const DownloadReadingDialogue = React.lazy(() => import("../../../../components/demo-download-dialogue"));

const SHARE_OPTIONS = [
  { value: "pdf", label: "PDF", icon: <CiFileOn size={16} /> },
  { value: "png", label: "PNG", icon: <CiImageOn size={16} /> },
];

export const READING_TYPES = [
  { value: "", label: "All Types" },
  { value: "blood_pressure", label: "Blood Pressure" },
  { value: "weight", label: "Weight" },
  { value: "blood_glucose", label: "Blood Glucose" },
  { value: "temperature", label: "Temperature" },
  { value: "pulse_ox", label: "Pulse Oximetry" },
  { value: "weight_change", label: "Weight Change" },
];

export const STATUS_LABEL = {
  not_reviewed: "Not reviewed",
  reviewed: "Reviewed",
  escalated: "Escalated",
};

// Dot color for a category the API computed (bp_category,
// heart_rate_category or category). Unknown/missing categories get no dot.
const CATEGORY_COLOR = {
  normal: "#25b46a",
  elevated: "#f59e0b",
  "stage 1": "#f59e0b",
  "stage 2": "#ef4444",
  "hypertensive crisis": "#b91c1c",
  hypotensive: "#f59e0b",
  bradycardia: "#f59e0b",
  tachycardia: "#ef4444",
};

const fmt = (value, unit, digits = 0) =>
  value === null || value === undefined || value === ""
    ? "—"
    : `${Number(value).toFixed(digits)}${unit ? ` ${unit}` : ""}`;

const Value = ({ text, category }) => {
  const color = category ? CATEGORY_COLOR[String(category).toLowerCase()] || "#94a3b8" : null;
  return (
    <span title={category || undefined} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      {color && <span className={styles.valueDot} style={{ background: color }} />}
      {text}
    </span>
  );
};

// Value columns per reading type (API "Reading object" value fields).
const VALUE_COLUMNS = {
  blood_pressure: [
    { label: "Systolic", render: (r) => <Value text={fmt(r.systolic, "mmHg")} category={r.bp_category} /> },
    { label: "Diastolic", render: (r) => <Value text={fmt(r.diastolic, "mmHg")} category={r.bp_category} /> },
    { label: "Heart Rate", render: (r) => <Value text={fmt(r.heart_rate, "bpm")} category={r.heart_rate_category} /> },
  ],
  weight: [{ label: "Weight", render: (r) => <Value text={fmt(r.weight, "lbs", 2)} category={r.category} /> }],
  blood_glucose: [{ label: "Glucose", render: (r) => <Value text={fmt(r.glucose, "mg/dL")} category={r.category} /> }],
  temperature: [{ label: "Temperature", render: (r) => <Value text={fmt(r.temperature, "°F", 1)} category={r.category} /> }],
  pulse_ox: [
    { label: "Oxygen", render: (r) => <Value text={fmt(r.oxygen, "%")} category={r.category} /> },
    { label: "Heart Rate", render: (r) => <Value text={fmt(r.heart_rate, "bpm")} /> },
  ],
  weight_change: [
    { label: "Weight Change", render: (r) => <Value text={fmt(r.weight_change, "lbs", 2)} category={r.category} /> },
    { label: "Period", render: (r) => r.period || "—" },
  ],
};

// Checkbox that can show the "some selected" dash.
const SelectBox = ({ checked, indeterminate, onChange, label }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className={styles.checkbox}
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      aria-label={label}
    />
  );
};

// "1/16/2026 3:05pm" - the time in light text, as in Figma.
const ReadingDate = ({ iso }) => {
  const [date, time] = formatReadingDate(iso).split(" ");
  return (
    <>
      {date} <span className={styles.readingTime}>{time}</span>
    </>
  );
};

// Clicking cycles Reviewed → Escalated → Not Reviewed for any reading,
// whatever it's saved as; the change is staged until Save.
const StatusPill = ({ reading, status, unsaved, onToggle }) => {
  const cls =
    status === "reviewed" ? styles.statusReviewed : status === "escalated" ? styles.statusEscalated : "";
  return (
    <button
      type="button"
      className={`${styles.statusPill} ${cls} ${unsaved ? styles.statusUnsaved : ""}`}
      onClick={() => onToggle(reading)}
      title="Click to change status"
    >
      {STATUS_LABEL[status] || status}
    </button>
  );
};

const ReadingGroup = ({ group, selectedIds, onSelect, statusChanges, onToggleStatus }) => {
  const [open, setOpen] = useState(true);
  const columns = VALUE_COLUMNS[group.reading_type] || [];

  // SCRUM-851 #4/#5: the cluster header shows the type and count only -
  // readings are selected one at a time (no group checkbox).
  return (
    <div>
      <div className={styles.groupHeader}>
        <span className={styles.groupTitle}>{group.label}</span>
        <button type="button" className={styles.groupToggle} onClick={() => setOpen((o) => !o)}>
          {group.total} Readings {open ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
        </button>
      </div>
      {open && (
        <table className={styles.readingsTable}>
          <thead>
            <tr>
              <th className={styles.checkCell} aria-label="Select" />
              <th>Reading Date</th>
              {columns.map((c) => (
                <th key={c.label}>{c.label}</th>
              ))}
              <th>Change Status</th>
            </tr>
          </thead>
          <tbody>
            {group.readings.map((reading) => {
              const staged = statusChanges[reading.id];
              return (
                <tr key={reading.id} className={reading.out_of_range ? styles.oorRow : ""}>
                  <td className={styles.checkCell}>
                    <SelectBox
                      checked={selectedIds.has(reading.id)}
                      onChange={(checked) => onSelect([reading.id], checked)}
                      label={`Select reading from ${formatReadingDate(reading.recorded_at)}`}
                    />
                  </td>
                  <td>
                    <ReadingDate iso={reading.recorded_at} />
                  </td>
                  {columns.map((c) => (
                    <td key={c.label}>{c.render(reading)}</td>
                  ))}
                  <td>
                    <StatusPill
                      reading={reading}
                      status={staged || reading.status}
                      unsaved={!!staged}
                      onToggle={onToggleStatus}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

// SCRUM-851: the Unreviewed tab of the session workspace - the patient's
// locked readings (API #9), grouped by type, with Total Unreviewed / Total
// OOR, a checkbox per reading (no group / All Readings checkbox), and a
// status pill per reading. Selection and staged statuses are owned by the
// workspace, which saves them.
const SessionReadingsPanel = ({
  patientId,
  data,
  isLoading,
  isError,
  readingType,
  onReadingTypeChange,
  selectedIds,
  onSelectionChange,
  statusChanges,
  onToggleStatus,
}) => {
  const [tab, setTab] = useState("unreviewed");
  // ChartsPanel ("All Data") state, as in the classic Readings Review.
  const [chartsOpen, setChartsOpen] = useState(true);
  const [patientType, setPatientType] = useState("RPM");
  const [chatBotType, setChatBotType] = useState("RPM");

  // Share / download - the same export as the classic Readings Review (its
  // export works per reading type, so "All Types" exports the first group's).
  const [shareOpen, setShareOpen] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState(null);

  useEffect(() => setTab("unreviewed"), [patientId]);

  const groups = data?.groups || [];
  const exportType = readingType || groups[0]?.reading_type || "blood_pressure";
  const exportRange = { startDate: "", endDate: "" };

  useQuery({
    queryKey: ["reading-review", "export", patientId, exportType, downloadFormat],
    queryFn: () => exportPatientReadings(patientId, downloadFormat, exportType, "1week", exportRange, true),
    enabled: !!downloadFormat,
    refetchOnWindowFocus: false,
    cacheTime: 0,
    staleTime: 0,
    retry: false,
    onSettled: () => setDownloadFormat(null),
    onSuccess: (file) => {
      const url = window.URL.createObjectURL(new Blob([file]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        buildReportFilename({ doctype: DOCTYPES.UNREVIEWED_READINGS, program: "RPM", ext: downloadFormat })
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Readings exported successfully");
    },
    onError: () => toast.error("Could not download readings"),
  });
  const allIds = groups.flatMap((g) => g.readings.map((r) => r.id));

  const select = (ids, checked) => {
    const next = new Set(selectedIds);
    ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
    onSelectionChange(next);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <div className={styles.readingsTabs}>
        <div className={styles.readingsTabGroup}>
          {[
            ["unreviewed", "Unreviewed"],
            ["all-data", "All Data"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={`${styles.readingsTab} ${tab === value ? styles.readingsTabActive : ""}`}
              onClick={() => setTab(value)}
            >
              {label}
            </button>
          ))}
        </div>
        {/* History tab: not part of this phase, hidden (SCRUM-851 #11). */}
      </div>

      <div className={styles.readingsCard}>
        {tab === "all-data" ? (
          <div style={{ flex: 1, minHeight: 0, overflow: "auto", margin: 10 }}>
            <ChartsPanel
              patientId={patientId}
              isOpen={chartsOpen}
              onClose={() => setChartsOpen(false)}
              handlePatientType={setPatientType}
              setChatBotType={setChatBotType}
              isCcmChatStarted={false}
              patientType={patientType}
              chatBotType={chatBotType}
            />
          </div>
        ) : (
          <>
            <div className={styles.readingsTotals}>
              <span>
                <span className={styles.readingsTotalsLabel}>Total Unreviewed :</span>{" "}
                <strong>{data?.total_unreviewed ?? "—"}</strong>
              </span>
              <span>
                Total OOR: <strong>{data?.total_oor ?? "—"}</strong>
              </span>
              <div className={styles.typeSelect}>
                <Select value={readingType} onChange={onReadingTypeChange}>
                  <SelectTrigger>
                    <SelectValue>
                      {READING_TYPES.find((t) => t.value === readingType)?.label || "All Types"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {READING_TYPES.map((t) => (
                      <SelectItem key={t.value || "all"} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className={styles.allReadingsRow}>
              {/* Heading only - no select-all (SCRUM-851 #5). */}
              <span className={styles.allReadingsLabel}>All Readings</span>
              <div className={styles.allReadingsTools}>
                <span className={styles.legend}>
                  <span className={styles.legendSwatch} /> = Out of range
                </span>
                <button
                  type="button"
                  className={styles.circleBtn}
                  onClick={() => setShareOpen(true)}
                  disabled={allIds.length === 0}
                  aria-label="Share"
                  title="Share"
                >
                  <ShareSvg width={20} height={20} />
                </button>
                <Dropdown
                  value={downloadFormat}
                  onChange={setDownloadFormat}
                  trigger={
                    <button
                      type="button"
                      className={styles.circleBtn}
                      disabled={allIds.length === 0}
                      aria-label="Download"
                      title="Download"
                    >
                      <DownloadIcon size={17} />
                    </button>
                  }
                >
                  {SHARE_OPTIONS.map((opt) => (
                    <DropdownItem key={opt.value} value={opt.value}>
                      {opt.icon}
                      {opt.label}
                    </DropdownItem>
                  ))}
                </Dropdown>
              </div>
            </div>

            <div className={styles.groups}>
              {isLoading ? (
                <div className={styles.emptyState}>Loading readings…</div>
              ) : isError ? (
                <div className={styles.emptyState}>Could not load readings.</div>
              ) : groups.length === 0 ? (
                <div className={styles.emptyState}>No readings for this patient.</div>
              ) : (
                groups.map((group) => (
                  <ReadingGroup
                    key={group.reading_type}
                    group={group}
                    selectedIds={selectedIds}
                    onSelect={select}
                    statusChanges={statusChanges}
                    onToggleStatus={onToggleStatus}
                  />
                ))
              )}
            </div>
          </>
        )}
      </div>

      <React.Suspense fallback={null}>
        <DownloadReadingDialogue
          isOpen={shareOpen}
          setIsOpen={setShareOpen}
          readingType={exportType}
          timeFrame="1week"
          dateRange={exportRange}
          unreviewed={true}
        />
      </React.Suspense>
    </div>
  );
};

export default SessionReadingsPanel;
